<template>
  <section>
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-3 lg:pt-6',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-6 lg:pb-12'
      ]"
    >
      <div class="flex flex-col gap-4 w-full">
        <div class="flex gap-3 items-center md:gap-5">
          <div class="relative shrink-0 size-9">
            <span
              class="absolute inset-0 rounded-full red-flag-badge-pulse bg-lifeboost-red"
              aria-hidden="true"
            />
            <div class="flex relative z-10 justify-center items-center text-base leading-7 text-white rounded-full size-9 bg-lifeboost-red font-ww-regular">
              {{ badgeNumber }}
            </div>
          </div>
          <span
            class="text-xs tracking-wider leading-5 uppercase text-[#F1574B] font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <div class="pt-1.5">
          <h2
            class="text-2xl font-bold leading-tight sm:text-3xl lg:text-34 lg:leading-42 font-ww-bold text-[#3B2314]"
            v-html="title"
          />
        </div>

        <div
          class="pt-2 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_ul]:list-disc [&_ul]:ml-6 [&_a]:font-ww-bold [&_a]:underline"
          v-html="introContent"
        />

        <div
          v-if="calloutTitle || calloutContent"
          class="relative overflow-hidden rounded-b-lg border border-lifeboost-red border-t-4 bg-[#F0E8D8] px-7 pb-6"
          :class="calloutIcon === 'coffee' ? 'pt-10' : 'pt-8'"
        >
          <nuxt-img
            v-if="stainImageSrc"
            :src="stainImageSrc"
            alt=""
            width="225"
            height="191"
            format="webp"
            loading="lazy"
            class="absolute -right-px -top-1 opacity-30 mix-blend-multiply pointer-events-none md:opacity-100"
          />

          <div
            class="flex relative z-10 flex-col gap-2.5"
            :class="calloutAsideImageSrc || showCalloutAsidePlaceholder ? 'lg:flex-row lg:gap-11 lg:items-center' : ''"
          >
            <div
              class="flex-1 min-w-0"
             
            >
              <h3
               :class="stainImageSrc ? 'md:pr-48' : ''"
                v-if="calloutTitle && !calloutIcon"
                class="text-xl font-bold leading-8 text-[#3B2314] font-ww-bold"
                v-html="calloutTitle"
              />
              <h3
                v-else-if="calloutTitle"
                class="flex gap-4 items-start text-xl font-bold leading-8 text-[#3B2314] font-ww-bold"
              >
                <svg
                  v-if="calloutIcon === 'coffee'"
                  class="shrink-0 size-6"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M11 18C9.05 18 7.396 17.321 6.038 15.963C4.68 14.605 4.00067 12.9507 4 11V5C4 4.45 4.196 3.97933 4.588 3.588C4.98 3.19667 5.45067 3.00067 6 3H18.5C19.4667 3 20.2917 3.34167 20.975 4.025C21.6583 4.70833 22 5.53333 22 6.5C22 7.46667 21.6583 8.29167 20.975 8.975C20.2917 9.65833 19.4667 10 18.5 10H18V11C18 12.95 17.321 14.6043 15.963 15.963C14.605 17.3217 12.9507 18.0007 11 18ZM6 8H16V5H6V8ZM18 8H18.5C18.9167 8 19.271 7.85433 19.563 7.563C19.855 7.27167 20.0007 6.91733 20 6.5C19.9993 6.08267 19.8537 5.72867 19.563 5.438C19.2723 5.14733 18.918 5.00133 18.5 5H18V8ZM4 21V19H20V21H4Z"
                    fill="black"
                  />
                </svg>

                <svg
                  v-else-if="calloutIcon === 'alert'"
                  class="shrink-0 mt-0.5 size-6 text-[#3B2314]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M12 2 1 21h22L12 2zm1 14h-2v-2h2v2zm0-4h-2V8h2v4z" />
                </svg>
                <span class="-mt-1" v-html="calloutTitle" />
              </h3>

              <div
                v-if="calloutContent"
                class="text-15 leading-6 text-[#4A4A4A] font-ww-regular [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_strong]:font-ww-bold [&_p+p]:mt-4 [&_sup]:text-xs"
                v-html="calloutContent"
              />

            </div>

            <div
              v-if="showCalloutAsidePlaceholder || calloutAsideImageSrc"
              class="mx-auto shrink-0 lg:mx-0"
            >
              <nuxt-img
                v-if="calloutAsideImageSrc"
                :src="calloutAsideImageSrc"
                :alt="calloutAsideImageAlt"
                width="212"
                height="222"
                format="webp"
                loading="lazy"
                class="object-contain w-full h-auto max-w-52"
              />
              <div
                v-else
                class="w-full max-w-52 h-72 rounded-lg bg-[#2A2A2A]"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <div
          v-if="outroContent"
          class="pt-3 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_sup]:text-xs [&_em]:italic [&_strong]:font-ww-bold [&_strong]:text-xl [&_strong]:text-[#3B2314]"
          v-html="outroContent"
        />

        <div
          v-if="showDivider"
          class="mt-10 h-px w-full bg-[#E0D5C5]"
          aria-hidden="true"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeNumber: {
    type: String,
    default: '1'
  },
  badgeText: {
    type: String,
    default: 'Red Flag No. 1'
  },
  title: {
    type: String,
    default: "Why Heart Health Is the #1 Predictor of How You'll Age"
  },
  introContent: {
    type: String,
    default: `<p>Men are significantly more likely to experience cardiovascular issues earlier in life. One in four American men will die from heart disease.</p>
<p>And the first heart attack often strikes a decade earlier than it does in women.</p>
<p>But research shows something remarkable…</p>`
  },
  stainImageSrc: {
    type: String,
    default: '/assets/images/top-right-img-block.png'
  },
  calloutIcon: {
    type: String,
    default: 'coffee',
    validator: (value) => ['', 'coffee', 'alert'].includes(value)
  },
  calloutTitle: {
    type: String,
    default: 'Is Coffee Helping or Hurting Your Heart?'
  },
  calloutContent: {
    type: String,
    default: `<p>Studies have shown men who drink clean, antioxidant-rich coffee daily have lower risks of:</p>
<ul>
  <li>Hypertension</li>
  <li>Heart failure</li>
  <li>Atrial fibrillation</li>
  <li>Cardiovascular-related mortality</li>
</ul>
<p>A 2021 study published in Circulation: Heart Failure found that drinking 1–2 cups of coffee per day was associated with a <strong>5–12% decreased risk of heart failure.</strong> Another large review in The American Journal of Clinical Nutrition concluded that moderate coffee consumption is linked to <strong>lower all-cause mortality</strong> and improved cardiovascular outcomes.</p>`
  },
  calloutAsideImageSrc: {
    type: String,
    default: ''
  },
  calloutAsideImageAlt: {
    type: String,
    default: ''
  },
  showCalloutAsidePlaceholder: {
    type: Boolean,
    default: false
  },
  outroContent: {
    type: String,
    default: `<p>One reason coffee is such a powerful tool in your total body health arsenal is due to its antioxidant content which can aid in lowering inflammation (a driver of heart disease) and reducing oxidative stress.<sup>8</sup></p>
<p>Of course, not all coffee is the same. That's why sourcing and testing matter. Some coffees may be exposed to unwanted chemicals during cultivation and processing, while others prioritize cleaner growing practices and rigorous quality standards.</p>
<p>Lifeboost Coffee's beans are single-origin, shade-grown, and tested for over 400 mycotoxins, heavy metals, and other contaminants.</p>
<p>That means every cup is crafted to deliver antioxidants without the same chemical burden found in conventional coffee.</p>
<p>For men, that matters a lot.</p>`
  },
  showDivider: {
    type: Boolean,
    default: true
  },
  paddingBottom: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingTop: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Red Flag Content',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '1'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 1'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: "Why Heart Health Is the #1 Predictor of How You'll Age"
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Men are significantly more likely to experience cardiovascular issues earlier in life. One in four American men will die from heart disease.</p>
<p>And the first heart attack often strikes a decade earlier than it does in women.</p>
<p>But research shows something remarkable…</p>`
    },
    {
      name: 'stainImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/top-right-img-block.png'
    },
    {
      name: 'calloutIcon',
      type: 'text',
      enum: ['', 'coffee', 'alert'],
      defaultValue: 'coffee'
    },
    {
      name: 'calloutTitle',
      type: 'text',
      defaultValue: 'Is Coffee Helping or Hurting Your Heart?'
    },
    {
      name: 'calloutContent',
      type: 'richText',
      defaultValue: `<p>Studies have shown men who drink clean, antioxidant-rich coffee daily have lower risks of:</p>
<ul>
  <li>Hypertension</li>
  <li>Heart failure</li>
  <li>Atrial fibrillation</li>
  <li>Cardiovascular-related mortality</li>
</ul>
<p>A 2021 study published in Circulation: Heart Failure found that drinking 1–2 cups of coffee per day was associated with a <strong>5–12% decreased risk of heart failure.</strong> Another large review in The American Journal of Clinical Nutrition concluded that moderate coffee consumption is linked to <strong>lower all-cause mortality</strong> and improved cardiovascular outcomes.</p>`
    },
    {
      name: 'calloutAsideImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: ''
    },
    {
      name: 'calloutAsideImageAlt',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'showCalloutAsidePlaceholder',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'outroContent',
      type: 'richText',
      defaultValue: `<p>One reason coffee is such a powerful tool in your total body health arsenal is due to its antioxidant content which can aid in lowering inflammation (a driver of heart disease) and reducing oxidative stress.<sup>8</sup></p>
<p>Of course, not all coffee is the same. That's why sourcing and testing matter. Some coffees may be exposed to unwanted chemicals during cultivation and processing, while others prioritize cleaner growing practices and rigorous quality standards.</p>
<p>Lifeboost Coffee's beans are single-origin, shade-grown, and tested for over 400 mycotoxins, heavy metals, and other contaminants.</p>
<p>That means every cup is crafted to deliver antioxidants without the same chemical burden found in conventional coffee.</p>
<p>For men, that matters a lot.</p>`
    },
    {
      name: 'showDivider',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
